import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Clock } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { blogPosts } from '../mock';

const BlogPost = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const post = blogPosts.find(p => p.id === parseInt(id));

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Post Not Found</h1>
          <Button onClick={() => navigate('/resources')}>Back to Resources</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#e0f2fe] via-white to-[#f0f9ff] -z-10" />
        <div className="max-w-4xl mx-auto">
          <Button
            variant="ghost"
            className="mb-6"
            onClick={() => navigate('/resources')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Resources
          </Button>
          
          <Badge className="bg-[#0066FF]/10 text-[#0066FF] hover:bg-[#0066FF]/20 border-0 mb-4">
            {post.category}
          </Badge>
          
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            {post.title}
          </h1>
          
          <div className="flex flex-wrap items-center gap-6 text-gray-600 mb-8">
            <div className="flex items-center space-x-2">
              <Calendar className="w-4 h-4" />
              <span>{new Date(post.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
            </div>
            <div className="flex items-center space-x-2">
              <User className="w-4 h-4" />
              <span>{post.author}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Clock className="w-4 h-4" />
              <span>{post.readTime}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="px-4 sm:px-6 lg:px-8 mb-12">
        <div className="max-w-4xl mx-auto">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-96 object-cover rounded-2xl shadow-xl"
          />
        </div>
      </section>

      {/* Article Content */}
      <section className="px-4 sm:px-6 lg:px-8 pb-20">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-600 leading-relaxed mb-6">
              {post.excerpt}
            </p>
            
            <div className="text-gray-700 leading-relaxed space-y-6">
              <p>
                The landscape of human resources is evolving at an unprecedented pace. As organizations adapt to new workplace dynamics, HR professionals find themselves at the forefront of transformation, leveraging technology and innovative strategies to drive business success.
              </p>
              
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Key Insights</h2>
              <p>
                Understanding the core principles behind effective HR management is essential for building resilient, engaged teams. Our research shows that companies implementing modern HR solutions see significant improvements in employee satisfaction and operational efficiency.
              </p>
              
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">The Path Forward</h2>
              <p>
                As we look to the future, it's clear that HR technology will continue to play a pivotal role in shaping workplace culture and driving organizational success. By embracing innovation while maintaining a human-centric approach, companies can create environments where both people and business thrive.
              </p>
              
              <p>
                The key is finding the right balance between automation and personalization, ensuring that technology enhances rather than replaces the human element that makes great workplaces truly exceptional.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Transform Your HR Operations?</h2>
          <p className="text-lg text-gray-600 mb-8">
            Discover how HRMQore can help your organization thrive
          </p>
          <Button
            size="lg"
            className="bg-gradient-to-r from-[#0066FF] to-[#0052CC] text-white hover:opacity-90"
            onClick={() => navigate('/contact')}
          >
            Request Demo
          </Button>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;
